import React, {
    useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import NearbyMap from './NearbyMap';
import NearbyRightPanel from './NearbyRightPanel';
import NearbyFilterBar from './NearbyFilterBar';
import NearbySort from './NearbySort';
import NearbySummary from './NearbySummary';
import NearbyCard from './NearbyCard';
import NearbyPlacesStrip from './NearbyPlacesStrip';
import { sortLocations } from './utils';
import './NearbyLocations.css';

function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < breakpoint);
        const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [breakpoint]);
    return isMobile;
}

/**
 * Root "Nearby Highlights" section for a property details page — combines
 * the map, filter/sort controls, quick-select strip and card grid, and
 * coordinates which place (or the property itself) is currently selected
 * across all of those child views.
 */
export default function NearbyLocations({
                                            locations        = [],
                                            propertyLocation = '',
                                            propertyName     = 'This Property',
                                            propertyImage    = '',
                                            propertySlug     = '',
                                            loading          = false,
                                            propertyLat      = null,
                                            propertyLng      = null,
                                        }) {
    const isMobile = useIsMobile();

    const allLocations = useMemo(
        () =>
            locations.map((loc, i) => ({
                ...loc,
                _stableId: loc.id !== undefined ? String(loc.id) : String(i),
                _index:    i,
            })),
        [locations],
    );

    const [activeFilter, setActiveFilter] = useState('All');
    const [sortMode,     setSortMode]     = useState('nearest');

    const panelCloseTimerRef = useRef(null);

    const [activeId,  setActiveId]  = useState(null);
    const [hoveredId, setHoveredId] = useState(null);

    const [isPanelOpen,     setIsPanelOpen]     = useState(false);
    const [isPropertyPanel, setIsPropertyPanel] = useState(false);
    const [travelMode,      setTravelMode]      = useState('DRIVING');

    const [showCards, setShowCards] = useState(false);
    const [propertyCoords,    setPropertyCoords]    = useState(
        propertyLat && propertyLng
            ? { lat: parseFloat(propertyLat), lng: parseFloat(propertyLng) }
            : null
    );

    useEffect(() => {
        if (propertyLat && propertyLng) {
            setPropertyCoords({
                lat: parseFloat(propertyLat),
                lng: parseFloat(propertyLng),
            });
        }
    }, [propertyLat, propertyLng]);

    const [directionsRequest, setDirectionsRequest] = useState(null);

    const filteredLocations = useMemo(() => {
        const base =
            activeFilter === 'All'
                ? allLocations
                : allLocations.filter((loc) =>
                    (loc.category || '').toLowerCase().includes(activeFilter.toLowerCase()),
                );
        return sortLocations(base, sortMode);
    }, [allLocations, activeFilter, sortMode]);

    const selectedPlace = useMemo(
        () => (activeId ? allLocations.find((l) => l._stableId === activeId) ?? null : null),
        [allLocations, activeId],
    );


    // Shared by card click, marker click and strip click: opens the detail
    // panel and requests directions so the map draws the route to the property.
    const handlePlaceSelect = useCallback(
        (sid) => {
            const place = allLocations.find((l) => l._stableId === sid);
            if (!place) return;

            setActiveId(sid);
            setIsPropertyPanel(false);
            setIsPanelOpen(true);

            const lat = parseFloat(place.latitude ?? place.lat);
            const lng = parseFloat(place.longitude ?? place.lng);
            if (!isNaN(lat) && !isNaN(lng) && propertyCoords) {
                setDirectionsRequest({ from: propertyCoords, to: { lat, lng } });
            }
        },
        [allLocations, propertyCoords],
    );

    const handlePropertySelect = useCallback(() => {
        setActiveId(null);
        setIsPropertyPanel(true);
        setIsPanelOpen(true);
        setDirectionsRequest(null);
    }, []);

    const handlePanelClose = useCallback(() => {
        setIsPanelOpen(false);
        setActiveId(null);
        setDirectionsRequest(null);
        clearTimeout(panelCloseTimerRef.current);
        panelCloseTimerRef.current = setTimeout(() => setIsPropertyPanel(false), 350);
    }, []);

    useEffect(() => () => clearTimeout(panelCloseTimerRef.current), []);

    const handleMapBackgroundClick = useCallback(() => {
        handlePanelClose();
    }, [handlePanelClose]);

    const handleFilterChange = useCallback((key) => {
        setActiveFilter(key);
        setActiveId(null);
        setIsPanelOpen(false);
        setDirectionsRequest(null);
    }, []);

    const handleTravelModeChange = useCallback((mode) => {
        setTravelMode(mode);
    }, []);

    if (loading) return (
        <section className="nearby-section" aria-label="Nearby Highlights">
            <h2 className="nearby-title">Nearby Highlights</h2>
            <div className="nearby-loading-skeleton">
                <div className="nearby-skel nearby-skel--strip" />
                <div className="nearby-skel nearby-skel--map"   />
                <div className="nearby-skel-cards">
                    {[1,2,3].map(i => <div key={i} className="nearby-skel nearby-skel--card" />)}
                </div>
            </div>
        </section>
    );

    if (!allLocations.length) return null;

    return (
        <section className="nearby-section" aria-label="Nearby Highlights">
            <h2 className="nearby-title">Nearby Highlights</h2>

            <NearbySummary locations={allLocations} />

            <NearbyFilterBar
                locations={allLocations}
                activeFilter={activeFilter}
                onFilterChange={handleFilterChange}
            />

            <div className="nearby-controls-row">
                <span className="nearby-results-count">
                    {filteredLocations.length} place
                    {filteredLocations.length !== 1 ? 's' : ''} found
                </span>
                <div className="nearby-controls-right">
                    <button
                        className={`nearby-cards-toggle${showCards ? '' : ' is-hidden'}`}
                        onClick={() => setShowCards(v => !v)}
                        type="button"
                        aria-expanded={showCards}
                        aria-controls="nearby-cards-grid"
                        title={showCards ? 'Hide place cards' : 'Show place cards'}
                    >
                        <span className="nearby-cards-toggle-icon" aria-hidden="true">
                            {showCards ? '⊟' : '⊞'}
                        </span>
                        <span>{showCards ? 'Hide Cards' : 'Show Cards'}</span>
                    </button>
                    <NearbySort sortMode={sortMode} onSortChange={setSortMode} />
                </div>
            </div>

            <NearbyPlacesStrip
                locations={filteredLocations}
                activeId={activeId}
                onSelect={handlePlaceSelect}
            />

            <div className={`nearby-map-panel-layout${isPanelOpen ? ' panel-open' : ''}`}>
                <div className="nearby-map-container">
                    <NearbyMap
                        locations={filteredLocations}
                        allLocations={allLocations}
                        propertyLocation={propertyLocation}
                        propertyName={propertyName}
                        activeId={activeId}
                        hoveredId={hoveredId}
                        activeFilter={activeFilter}
                        isPanelOpen={isPanelOpen}
                        travelMode={travelMode}
                        directionsRequest={directionsRequest}
                        onMarkerClick={handlePlaceSelect}
                        onPropertyMarkerClick={handlePropertySelect}
                        onPropertyCoordsReady={(coords) => {
                            if (!propertyLat || !propertyLng) setPropertyCoords(coords);
                        }}
                        onMapBackgroundClick={handleMapBackgroundClick}
                    />
                </div>

                <NearbyRightPanel
                    isOpen={isPanelOpen}
                    onClose={handlePanelClose}
                    selectedPlace={selectedPlace}
                    isPropertyPanel={isPropertyPanel}
                    propertyName={propertyName}
                    propertyLocation={propertyLocation}
                    propertyImage={propertyImage}
                    propertyCoords={propertyCoords}
                    travelMode={travelMode}
                    onTravelModeChange={handleTravelModeChange}
                    isMobile={isMobile}
                    allLocations={allLocations}
                    propertySlug={propertySlug}
                />
            </div>

            <div
                id="nearby-cards-grid"
                className={`nearby-grid-wrapper${showCards ? ' is-visible' : ' is-collapsed'}`}
            >
                <div className="nearby-grid">
                    {filteredLocations.length === 0 ? (
                        <div className="nearby-empty">
                            <span className="nearby-empty-icon">🔍</span>
                            <p className="nearby-empty-text">No nearby places found for this filter.</p>
                        </div>
                    ) : (
                        filteredLocations.map((item) => (
                            <NearbyCard
                                key={item._stableId}
                                item={item}
                                isActive={activeId === item._stableId}
                                onHover={setHoveredId}
                                onCardClick={handlePlaceSelect}
                            />
                        ))
                    )}
                </div>
            </div>
        </section>
    );
}
